import { DEPARTMENT_BASE_PATH } from '@pages/departments/config';

/**
 * Path segments of the employees tree — the single source for the
 * route config, the employees home tabs and in-app links, so no
 * caller refers to the segments directly.
 */

export const EMPLOYEES_PATH_SEGMENT = 'employees' as const;
export const TABLE_PATH_SEGMENT = 'table' as const;
export const WAGES_PATH_SEGMENT = 'wages' as const;
export const NEW_PATH_SEGMENT = 'new' as const;
export const SETTINGS_PATH_SEGMENT = 'settings' as const;
export const EMPLOYEE_SETTINGS_TABS = ['invite'] as const;

export type EmployeeSettingsTab = (typeof EMPLOYEE_SETTINGS_TABS)[number];

export const DEFAULT_EMPLOYEE_SETTINGS_TAB: EmployeeSettingsTab = 'invite';

const EMPLOYEE_SETTINGS_TAB_SET: ReadonlySet<string> = new Set(
    EMPLOYEE_SETTINGS_TABS,
);

export const isEmployeeSettingsTab = (
    value: string | undefined,
): value is EmployeeSettingsTab =>
    value !== undefined && EMPLOYEE_SETTINGS_TAB_SET.has(value);

const employeesPath = (departmentId: string, suffix = '' as string): string =>
    `${DEPARTMENT_BASE_PATH}/${departmentId}/${EMPLOYEES_PATH_SEGMENT}${suffix}`;

/**
 * Canonical paths of the employees section
 * (`/departments/:departmentId/employees`, see `src/pages/employees`).
 *
 * The employees home (list, bulk table and wages tabs) is rendered by
 * the employees home shell; the employee settings currently hold the
 * invite tab. The remaining employees subviews still live in
 * `src/views` (create, edit).
 */
export const EMPLOYEES_PATHS = {
    /** Employee list (employees home shell index). */
    employees: (departmentId: string): string => employeesPath(departmentId),
    /** Bulk employee table. */
    employeesTable: (departmentId: string): string =>
        employeesPath(departmentId, `/${TABLE_PATH_SEGMENT}`),
    /** Create employee. */
    employeeNew: (departmentId: string): string =>
        employeesPath(departmentId, `/${NEW_PATH_SEGMENT}`),
    /** Employee wages (Timelønn). */
    employeesWages: (departmentId: string): string =>
        employeesPath(departmentId, `/${WAGES_PATH_SEGMENT}`),
    /** Edit employee. */
    employee: (departmentId: string, employeeId: string): string =>
        employeesPath(departmentId, `/${employeeId}`),
    /** Employee settings tabs (invite, …). */
    employeeSettings: (departmentId: string): string =>
        employeesPath(departmentId, `/${SETTINGS_PATH_SEGMENT}`),
    /** Single employee settings tab. */
    employeeSettingsTab: (departmentId: string, tab: string): string =>
        employeesPath(departmentId, `/${SETTINGS_PATH_SEGMENT}/${tab}`),
} as const;
